'use client';

import { c as _c } from "react/compiler-runtime";
import { useCallback, useState, useRef, useEffect } from 'react';
import { GoogleMap, useJsApiLoader, MarkerF, InfoWindowF, PolygonF, OverlayViewF, OverlayView } from '@react-google-maps/api';
import { getListingHref, type MLSProperty } from '@/lib/listings';
import { grayscaleMapStyles } from '@/lib/mapStyles';
interface ListingsMapProps {
  listings: MLSProperty[];
  onDrawComplete?: (filteredListings: MLSProperty[]) => void;
  onDrawClear?: () => void;
  hasLocationFilter?: boolean; // true when city or neighborhood is selected
  googleMapsApiKey?: string;
}
function formatPrice(price: number | null): string {
  if (!price) return 'Price N/A';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(price);
}

// Format price for map markers (e.g., $4.9M, $799K)
function formatPriceShort(price: number | null): string {
  if (!price) return 'N/A';
  if (price >= 1000000) {
    const millions = price / 1000000;
    // Show one decimal if not a whole number
    return `$${millions % 1 === 0 ? millions.toFixed(0) : millions.toFixed(1)}M`;
  }
  if (price >= 1000) {
    const thousands = price / 1000;
    return `$${thousands % 1 === 0 ? thousands.toFixed(0) : thousands.toFixed(0)}K`;
  }
  return `$${price}`;
}

// Sotheby's blue color
const SOTHEBYS_BLUE = '#00254a';

// Price flag marker component
interface PriceMarkerProps {
  position: google.maps.LatLngLiteral;
  price: number | null;
  status: string;
  onClick: () => void;
  isSelected: boolean;
}
function PriceMarker(t0) {
  const $ = _c(20);
  const {
    position,
    price,
    status,
    onClick,
    isSelected
  } = t0;
  const isClosed = status === "Closed";
  const t1 = `cursor-pointer transition-transform hover:scale-110 ${isSelected ? "scale-110 z-10" : ""}`;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      position: "relative"
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const t3 = `px-2 py-1 rounded-sm text-white text-xs font-semibold whitespace-nowrap shadow-md ${isClosed ? "opacity-60" : ""}`;
  const t4 = isSelected ? "#c5a047" : SOTHEBYS_BLUE;
  let t5;
  if ($[1] !== t4) {
    t5 = {
      backgroundColor: t4,
      minWidth: "45px",
      textAlign: "center"
    };
    $[1] = t4;
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  let t6;
  if ($[3] !== price) {
    t6 = formatPriceShort(price);
    $[3] = price;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  let t7;
  if ($[5] !== t3 || $[6] !== t5 || $[7] !== t6) {
    t7 = <div className={t3} style={t5}>{t6}</div>;
    $[5] = t3;
    $[6] = t5;
    $[7] = t6;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const t8 = `8px solid ${isSelected ? "#c5a047" : SOTHEBYS_BLUE}`;
  const t9 = isClosed ? 0.6 : 1;
  let t10;
  if ($[9] !== t8 || $[10] !== t9) {
    t10 = <div className="mx-auto" style={{
      width: 0,
      height: 0,
      borderLeft: "6px solid transparent",
      borderRight: "6px solid transparent",
      borderTop: t8,
      opacity: t9
    }} />;
    $[9] = t8;
    $[10] = t9;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  let t11;
  if ($[12] !== onClick || $[13] !== t1 || $[14] !== t10 || $[15] !== t7) {
    t11 = <div onClick={onClick} className={t1} style={t2}>{t7}{t10}</div>;
    $[12] = onClick;
    $[13] = t1;
    $[14] = t10;
    $[15] = t7;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  let t12;
  if ($[17] !== position || $[18] !== t11) {
    t12 = <OverlayViewF position={position} mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET} getPixelPositionOffset={_temp}>{t11}</OverlayViewF>;
    $[17] = position;
    $[18] = t11;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  return t12;
}

// Check if a point is inside a polygon using ray casting algorithm
function _temp(width, height) {
  return {
    x: -(width / 2),
    y: -height
  };
}
function isPointInPolygon(point: {
  lat: number;
  lng: number;
}, polygon: google.maps.LatLngLiteral[]): boolean {
  let inside = false;
  const x = point.lng;
  const y = point.lat;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i].lng;
    const yi = polygon[i].lat;
    const xj = polygon[j].lng;
    const yj = polygon[j].lat;
    if (yi > y !== yj > y && x < (xj - xi) * (y - yi) / (yj - yi) + xi) {
      inside = !inside;
    }
  }
  return inside;
}
const mapContainerStyle = {
  width: '100%',
  height: '100%'
};
const mapOptions: google.maps.MapOptions = {
  disableDefaultUI: false,
  zoomControl: true,
  streetViewControl: false,
  mapTypeControl: false,
  fullscreenControl: true,
  styles: grayscaleMapStyles
};
const polygonOptions = {
  fillColor: SOTHEBYS_BLUE,
  fillOpacity: 0.2,
  strokeColor: SOTHEBYS_BLUE,
  strokeOpacity: 0.8,
  strokeWeight: 2
};
export default function ListingsMap(t0) {
  const $ = _c(73);
  const {
    listings,
    onDrawComplete,
    onDrawClear,
    hasLocationFilter,
    googleMapsApiKey
  } = t0;
  const [selectedListing, setSelectedListing] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [drawingPoints, setDrawingPoints] = useState(t1);
  const [completedPolygon, setCompletedPolygon] = useState(null);
  const mapRef = useRef(null);
  const apiKey = googleMapsApiKey || process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY || "";
  let t2;
  if ($[1] !== apiKey) {
    t2 = {
      googleMapsApiKey: apiKey
    };
    $[1] = apiKey;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const {
    isLoaded,
    loadError
  } = useJsApiLoader(t2);
  let listingsWithCoords;
  let t3;
  if ($[3] !== listings) {
    listingsWithCoords = listings.filter(_temp2);
    t3 = listingsWithCoords.length > 0 ? {
      lat: listingsWithCoords.reduce(_temp3, 0) / listingsWithCoords.length,
      lng: listingsWithCoords.reduce(_temp4, 0) / listingsWithCoords.length
    } : {
      lat: 39.1911,
      lng: -106.8175
    };
    $[3] = listings;
    $[4] = listingsWithCoords;
    $[5] = t3;
  } else {
    listingsWithCoords = $[4];
    t3 = $[5];
  }
  const center = t3;
  let t4;
  if ($[6] !== hasLocationFilter || $[7] !== listingsWithCoords) {
    t4 = map => {
      mapRef.current = map;
      if (listingsWithCoords.length > 0) {
        const bounds = new google.maps.LatLngBounds();
        listingsWithCoords.forEach(listing_0 => {
          bounds.extend({
            lat: listing_0.latitude,
            lng: listing_0.longitude
          });
        });
        map.fitBounds(bounds);
        if (hasLocationFilter) {
          google.maps.event.addListenerOnce(map, "bounds_changed", () => {
            const currentZoom = map.getZoom();
            if (currentZoom && currentZoom < 13) {
              map.setZoom(13);
            }
          });
        }
      }
    };
    $[6] = hasLocationFilter;
    $[7] = listingsWithCoords;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const onLoad = t4;
  let t5;
  if ($[9] !== isDrawing) {
    t5 = e => {
      if (!isDrawing || !e.latLng) {
        return;
      }
      const newPoint = {
        lat: e.latLng.lat(),
        lng: e.latLng.lng()
      };
      setDrawingPoints(prev => [...prev, newPoint]);
    };
    $[9] = isDrawing;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const handleMapClick = t5;
  let t6;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      setIsDrawing(true);
      setDrawingPoints([]);
      setCompletedPolygon(null);
      if (mapRef.current) {
        mapRef.current.setOptions({
          draggableCursor: "crosshair"
        });
      }
    };
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const startDrawing = t6;
  let t7;
  if ($[12] !== drawingPoints || $[13] !== listingsWithCoords || $[14] !== onDrawComplete) {
    t7 = () => {
      if (drawingPoints.length >= 3) {
        setCompletedPolygon(drawingPoints);
        setIsDrawing(false);
        if (mapRef.current) {
          mapRef.current.setOptions({
            draggableCursor: null
          });
        }
        const filteredListings = listingsWithCoords.filter(listing_1 => isPointInPolygon({
          lat: listing_1.latitude,
          lng: listing_1.longitude
        }, drawingPoints));
        if (onDrawComplete) {
          onDrawComplete(filteredListings);
        }
      }
    };
    $[12] = drawingPoints;
    $[13] = listingsWithCoords;
    $[14] = onDrawComplete;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const completeDrawing = t7;
  let t8;
  if ($[16] !== onDrawClear) {
    t8 = () => {
      setIsDrawing(false);
      setDrawingPoints([]);
      setCompletedPolygon(null);
      if (mapRef.current) {
        mapRef.current.setOptions({
          draggableCursor: null
        });
      }
      if (onDrawClear) {
        onDrawClear();
      }
    };
    $[16] = onDrawClear;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const clearDrawing = t8;
  let t9;
  if ($[18] !== clearDrawing || $[19] !== isDrawing) {
    t9 = () => {
      const handleKeyDown = e_0 => {
        if (e_0.key === "Escape" && isDrawing) {
          clearDrawing();
        }
      };
      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    };
    $[18] = clearDrawing;
    $[19] = isDrawing;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== isDrawing) {
    t10 = [isDrawing];
    $[21] = isDrawing;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  useEffect(t9, t10);
  if (!apiKey || loadError) {
    let t11;
    if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
      t11 = <div className="w-full h-full bg-gray-100 flex items-center justify-center"><div className="text-center text-gray-500"><p>Map unavailable</p><p className="text-sm mt-1">Switch to list view to browse properties</p></div></div>;
      $[23] = t11;
    } else {
      t11 = $[23];
    }
    return t11;
  }
  if (!isLoaded) {
    let t11;
    if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
      t11 = <div className="w-full h-full bg-gray-100 flex items-center justify-center"><div className="text-gray-500">Loading map...</div></div>;
      $[24] = t11;
    } else {
      t11 = $[24];
    }
    return t11;
  }
  let t11;
  if ($[25] !== completedPolygon || $[26] !== isDrawing) {
    t11 = !isDrawing && !completedPolygon && <button onClick={startDrawing} className="px-4 py-2 bg-white rounded-md shadow-md text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center gap-2"><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a2 2 0 110-4h1a1 1 0 001-1V7a1 1 0 011-1h3a1 1 0 001-1V4z" /></svg>Draw to Search</button>;
    $[25] = completedPolygon;
    $[26] = isDrawing;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== clearDrawing || $[29] !== completeDrawing || $[30] !== drawingPoints.length || $[31] !== isDrawing) {
    t12 = isDrawing && <><div className="px-4 py-2 bg-blue-600 rounded-md shadow-md text-sm font-medium text-white">Click on map to draw area</div><div className="flex gap-2"><button onClick={completeDrawing} disabled={drawingPoints.length < 3} className={`px-4 py-2 rounded-md shadow-md text-sm font-medium flex items-center gap-2 ${drawingPoints.length >= 3 ? "bg-green-600 text-white hover:bg-green-700" : "bg-gray-300 text-gray-500 cursor-not-allowed"}`}><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>Complete ({drawingPoints.length} points)</button><button onClick={clearDrawing} className="px-4 py-2 bg-red-600 text-white rounded-md shadow-md text-sm font-medium hover:bg-red-700 flex items-center gap-2"><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>Cancel</button></div><p className="text-xs text-gray-600 bg-white/90 px-2 py-1 rounded">Press Esc to cancel</p></>;
    $[28] = clearDrawing;
    $[29] = completeDrawing;
    $[30] = drawingPoints.length;
    $[31] = isDrawing;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  let t13;
  if ($[33] !== clearDrawing || $[34] !== completedPolygon) {
    t13 = completedPolygon && <button onClick={clearDrawing} className="px-4 py-2 bg-white rounded-md shadow-md text-sm font-medium text-red-600 hover:bg-red-50 flex items-center gap-2"><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>Clear Area Search</button>;
    $[33] = clearDrawing;
    $[34] = completedPolygon;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  let t14;
  if ($[36] !== t11 || $[37] !== t12 || $[38] !== t13) {
    t14 = <div className="absolute top-4 left-4 z-10 flex flex-col gap-2">{t11}{t12}{t13}</div>;
    $[36] = t11;
    $[37] = t12;
    $[38] = t13;
    $[39] = t14;
  } else {
    t14 = $[39];
  }
  const t15 = !isDrawing;
  let t16;
  if ($[40] !== t15) {
    t16 = {
      ...mapOptions,
      draggable: t15
    };
    $[40] = t15;
    $[41] = t16;
  } else {
    t16 = $[41];
  }
  let t17;
  if ($[42] !== drawingPoints || $[43] !== isDrawing) {
    t17 = isDrawing && drawingPoints.length >= 2 && <PolygonF paths={drawingPoints} options={{
      ...polygonOptions,
      fillOpacity: 0.1,
      strokeWeight: 2,
      strokeOpacity: 0.5
    }} />;
    $[42] = drawingPoints;
    $[43] = isDrawing;
    $[44] = t17;
  } else {
    t17 = $[44];
  }
  let t18;
  if ($[45] !== completedPolygon) {
    t18 = completedPolygon && <PolygonF paths={completedPolygon} options={polygonOptions} />;
    $[45] = completedPolygon;
    $[46] = t18;
  } else {
    t18 = $[46];
  }
  let t19;
  if ($[47] !== drawingPoints || $[48] !== isDrawing) {
    t19 = isDrawing && drawingPoints.map(_temp5);
    $[47] = drawingPoints;
    $[48] = isDrawing;
    $[49] = t19;
  } else {
    t19 = $[49];
  }
  let t20;
  if ($[50] !== listingsWithCoords || $[51] !== selectedListing?.id) {
    let t21;
    if ($[53] !== selectedListing?.id) {
      t21 = listing_2 => <PriceMarker key={listing_2.id} position={{
        lat: listing_2.latitude,
        lng: listing_2.longitude
      }} price={listing_2.list_price} status={listing_2.status} onClick={() => setSelectedListing(listing_2)} isSelected={selectedListing?.id === listing_2.id} />;
      $[53] = selectedListing?.id;
      $[54] = t21;
    } else {
      t21 = $[54];
    }
    t20 = listingsWithCoords.map(t21);
    $[50] = listingsWithCoords;
    $[51] = selectedListing?.id;
    $[52] = t20;
  } else {
    t20 = $[52];
  }
  let t21;
  if ($[55] !== selectedListing) {
    t21 = selectedListing && <InfoWindowF position={{
      lat: selectedListing.latitude,
      lng: selectedListing.longitude
    }} onCloseClick={() => setSelectedListing(null)}><div className="min-w-[200px] p-1"><p className="font-bold text-lg text-gray-900">{formatPrice(selectedListing.list_price)}</p><p className="text-sm text-gray-600">{selectedListing.address}</p><p className="text-sm text-gray-500">{selectedListing.bedrooms} bd | {selectedListing.bathrooms} ba{selectedListing.square_feet && ` | ${selectedListing.square_feet.toLocaleString()} sqft`}</p><p className="text-xs text-gray-400 mt-1">MLS# {selectedListing.mls_number}</p><a href={getListingHref(selectedListing)} className="mt-2 inline-block text-sm text-[#00254a] hover:underline font-medium">View Details</a></div></InfoWindowF>;
    $[55] = selectedListing;
    $[56] = t21;
  } else {
    t21 = $[56];
  }
  let t22;
  if ($[57] !== center || $[58] !== handleMapClick || $[59] !== onLoad || $[60] !== t16 || $[61] !== t17 || $[62] !== t18 || $[63] !== t19 || $[64] !== t20 || $[65] !== t21) {
    t22 = <GoogleMap mapContainerStyle={mapContainerStyle} center={center} zoom={10} options={t16} onLoad={onLoad} onClick={handleMapClick}>{t17}{t18}{t19}{t20}{t21}</GoogleMap>;
    $[57] = center;
    $[58] = handleMapClick;
    $[59] = onLoad;
    $[60] = t16;
    $[61] = t17;
    $[62] = t18;
    $[63] = t19;
    $[64] = t20;
    $[65] = t21;
    $[66] = t22;
  } else {
    t22 = $[66];
  }
  let t23;
  if ($[67] !== listingsWithCoords.length) {
    t23 = listingsWithCoords.length === 0 && <div className="absolute inset-0 flex items-center justify-center bg-gray-100/80 pointer-events-none"><p className="text-gray-500">No properties with location data</p></div>;
    $[67] = listingsWithCoords.length;
    $[68] = t23;
  } else {
    t23 = $[68];
  }
  let t24;
  if ($[69] !== t14 || $[70] !== t22 || $[71] !== t23) {
    t24 = <div className="relative w-full h-full">{t14}{t22}{t23}</div>;
    $[69] = t14;
    $[70] = t22;
    $[71] = t23;
    $[72] = t24;
  } else {
    t24 = $[72];
  }
  return t24;
}
function _temp5(point, index) {
  return <MarkerF key={`draw-point-${index}`} position={point} icon={{
    path: google.maps.SymbolPath.CIRCLE,
    scale: 6,
    fillColor: SOTHEBYS_BLUE,
    fillOpacity: 1,
    strokeColor: "#ffffff",
    strokeWeight: 2
  }} />;
}
function _temp4(sum_0, l_0) {
  return sum_0 + (l_0.longitude || 0);
}
function _temp3(sum, l) {
  return sum + (l.latitude || 0);
}
function _temp2(listing) {
  return typeof listing.latitude === "number" && typeof listing.longitude === "number" && isFinite(listing.latitude) && isFinite(listing.longitude);
}